import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpContextToken,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { exhaustMap, filter, finalize, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments';

import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';
import { SignatureV4 } from '@smithy/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { LocalStorageService } from 'cc-components-lib/src/lib/cc-services';
export const CUSTOMIZER_TOKEN = new HttpContextToken(() => false);
export const REQUIRED_TOKEN = new HttpContextToken(() => false);

interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  expiration: string;
}

@Injectable({
  providedIn: 'root',
})
export class CognitoIdentityPoolService implements HttpInterceptor {
  private credentialsSubject = new BehaviorSubject<AwsCredentials | null>(null);
  private requestInProgress = false;
  private readonly IDENTITY_POOL_ID = environment.identityPoolId;
  private readonly REGION = environment.identityPoolRegion;
  private readonly STORAGE_KEY = btoa('@AWS_CREDENTIALS');
  private readonly PATH_REQUIRED_TOKEN: string[] = [
    environment.lambdaApiUrl,
    '/auth/v1',
  ].filter((path) => path && path.trim() !== '');
  private cognitoClient = new CognitoIdentityClient({ region: this.REGION });

  constructor(private localStorageService: LocalStorageService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.requestNeedToken(req.url, req.context)) {
      return next.handle(req);
    }

    const cachedCredentials = this.getCachedCredentials();
    if (cachedCredentials) {
      return this.handleRequestWithSignedHeaders(req, cachedCredentials, next);
    }

    if (!this.requestInProgress) {
      this.requestInProgress = true;
      this.credentialsSubject.next(null);
      return this.getAwsCredentials().pipe(
        exhaustMap((credentials) => {
          this.credentialsSubject.next(credentials);
          this.requestInProgress = false;
          return this.handleRequestWithSignedHeaders(req, credentials, next);
        }),
        finalize(() => {
          this.requestInProgress = false;
        }),
      );
    }

    return this.credentialsSubject.asObservable().pipe(
      filter((credentials) => credentials !== null),
      take(1),
      switchMap((credentials) =>
        this.handleRequestWithSignedHeaders(
          req,
          credentials as AwsCredentials,
          next,
        ),
      ),
    );
  }

  /**
   * Obtiene credenciales de AWS Identity Pool
   */
  private getAwsCredentials(): Observable<AwsCredentials> {
    return from(this.fetchAwsCredentials());
  }

  /**
   * Obtiene credenciales de AWS Identity Pool
   */
  private async fetchAwsCredentials(): Promise<AwsCredentials> {
    try {
      // Obtener el ID de la identidad
      const identityCommand = new GetIdCommand({
        IdentityPoolId: this.IDENTITY_POOL_ID,
      });
      const identityResponse = await this.cognitoClient.send(identityCommand);

      if (!identityResponse.IdentityId) {
        throw new Error('No se pudo obtener el ID de la identidad.');
      }

      // Obtener las credenciales para la identidad
      const credentialsCommand = new GetCredentialsForIdentityCommand({
        IdentityId: identityResponse.IdentityId,
      });
      const credentialsResponse = await this.cognitoClient.send(
        credentialsCommand,
      );

      if (!credentialsResponse.Credentials) {
        throw new Error('No se obtuvieron credenciales de AWS.');
      }

      const { AccessKeyId, SecretKey, SessionToken, Expiration } =
        credentialsResponse.Credentials;

      const credentials: AwsCredentials = {
        accessKeyId: AccessKeyId ?? '',
        secretAccessKey: SecretKey ?? '',
        sessionToken: SessionToken ?? '',
        expiration: Expiration ? Expiration.toISOString() : '',
      };

      // Guardar las credenciales en el almacenamiento local
      this.localStorageService.setLocalStorageCrypt(
        this.STORAGE_KEY,
        JSON.stringify(credentials),
      );

      return credentials;
    } catch (error) {
      // Verificar si el error es una instancia de Error
      if (error instanceof Error) {
        console.error('Error obteniendo credenciales:', error.message);
        throw new Error(`Error al obtener las credenciales: ${error.message}`);
      } else {
        // Si el error no es una instancia de Error, lanzar un error genérico
        console.error('Error desconocido al obtener credenciales:', error);
        throw new Error('Error desconocido al obtener las credenciales.');
      }
    }
  }

  /**
   * Obtiene credenciales almacenadas en localStorage si aún no han expirado.
   */
  private getCachedCredentials(): AwsCredentials | null {
    const storedCredentials = this.localStorageService.getLocalStorageCrypt(
      this.STORAGE_KEY,
    );
    if (!storedCredentials) return null;

    const credentials: AwsCredentials = JSON.parse(storedCredentials);
    if (new Date(credentials.expiration) > new Date()) {
      return credentials;
    }

    return null;
  }

  /**
   * Firma la petición con Signature V4 y la envía.
   */
  private async signRequest(
    req: HttpRequest<any>,
    credentials: AwsCredentials,
  ): Promise<HttpRequest<any>> {
    const url = new URL(req.url);
    const signer = new SignatureV4({
      service: 'execute-api',
      region: this.REGION,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
      sha256: Sha256,
    });

    const signedRequest = await signer.sign({
      method: req.method,
      protocol: url.protocol,
      hostname: url.hostname,
      path: url.pathname,
      headers: {},
    });

    return req.clone({
      setHeaders: { ...signedRequest.headers, 'X-Ciencuadras-Scope': 'public' },
    });
  }

  /**
   * Maneja la petición HTTP firmada.
   */
  private handleRequestWithSignedHeaders(
    req: HttpRequest<any>,
    credentials: AwsCredentials,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return from(this.signRequest(req, credentials)).pipe(
      switchMap((signedRequest) => next.handle(signedRequest)),
    );
  }

  /**
   * Verifica si la petición necesita ser autenticada.
   * @param {string} url
   * @returns {boolean}
   */
  private requestNeedToken(
    url: string,
    context?: HttpRequest<any>['context'],
  ): boolean {
    if (context && context.get(REQUIRED_TOKEN)) return true;
    return this.PATH_REQUIRED_TOKEN.some(
      (path) => url.includes(path) || url.indexOf(path) !== -1,
    );
  }
}
